import React, { useRef, useState, useMemo } from 'react'
import { Badge } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import BasicFilterPopover from 'shared/column-filters/BasicFilterPopover'
import BooleanFilterPopover from 'shared/column-filters/BooleanFilterPopover'
import DateFilterPopover from 'shared/column-filters/DateFilterPopover'
import MinMaxFilterPopover from 'shared/column-filters/MinMaxFilterPopover'
import SelectFilterPopover from 'shared/column-filters/SelectFilterPopover'
import MultiSelectFilterPopover from 'shared/column-filters/MultiSelectFilterPopover'
import MccSelectFilterPopover from 'shared/column-filters/MccSelectFilterPopover'
import GreaterThanFilterPopover from 'shared/column-filters/GreaterThanFilterPopover'
import ReferenceFilterPopover from 'shared/column-filters/ReferenceFilterPopover'
import VendorSearch from 'dashboard/filters/vendors'
import moment from 'moment'

/**
 * FilterEditor - A component that displays a filter inline for editing
 *
 * @param {Object} column - The column configuration with filter_type, accessor_key, etc.
 * @param {Object} params - The current filter parameters
 * @param {Function} setParams - Function to update filter parameters
 * @param {Function} getOptions - Optional function to get select options
 * @param {Array} options - Optional array of options for select filters
 * @param {String} label - Display label for the filter
 * @param {Boolean} isUserCreator - Whether the user can edit (default: true)
 * @param {Boolean} requiresChange - Whether this filter must be changed from its original value
 * @param {Boolean} hasBeenModified - Whether this filter has been modified from its original value
 * @param {Boolean} hideApplyButton - Whether to hide the Apply button (default: false)
 * @param {Object} initialTemplateFilterValues - The initial template values to show as defaults
 */
function FilterEditor({
  column,
  params,
  setParams,
  getOptions,
  options = [],
  label,
  isUserCreator = true,
  requiresChange = false,
  hasBeenModified = false,
  hideApplyButton = false,
  initialTemplateFilterValues = {}
}) {
  const childHandleApplyRef = useRef(null)
  const [isExpanded, setIsExpanded] = useState(requiresChange && !hasBeenModified)

  // Format the filter value for display in collapsed state
  const formatFilterValue = useMemo(() => {
    const accessorKey = column.accessor_key
    const prefix = column.section_abbreviation?.toLowerCase() || ''
    const suffix = accessorKey.slice(prefix.length)
    const minKey = `${prefix}Min${suffix}`
    const maxKey = `${prefix}Max${suffix}`

    // For min_max and min_max_percent filters
    if (['min_max', 'min_max_percent'].includes(column.filter_type)) {
      const minVal = params[minKey]
      const maxVal = params[maxKey]
      const isPercent = column.filter_type === 'min_max_percent'

      if (minVal && maxVal) {
        return `${isPercent ? minVal * 100 : minVal} - ${isPercent ? maxVal * 100 : maxVal}${isPercent ? '%' : ''}`
      } else if (minVal) {
        return `≥ ${isPercent ? minVal * 100 : minVal}${isPercent ? '%' : ''}`
      } else if (maxVal) {
        return `≤ ${isPercent ? maxVal * 100 : maxVal}${isPercent ? '%' : ''}`
      }
    }

    // For date filters
    if (column.filter_type === 'date') {
      const optionKey = `${accessorKey}Option`
      const option = params[optionKey]
      const minDate = params[minKey]
      const maxDate = params[maxKey]

      if (option && option !== 'any') {
        return option
      } else if (minDate || maxDate) {
        const dateFormat = 'MM/DD/YYYY'
        if (minDate && maxDate) {
          return `${moment(minDate).format(dateFormat)} - ${moment(maxDate).format(dateFormat)}`
        } else if (minDate) {
          return `After ${moment(minDate).format(dateFormat)}`
        } else if (maxDate) {
          return `Before ${moment(maxDate).format(dateFormat)}`
        }
      }
    }

    // For boolean filters
    if (column.filter_type === 'boolean') {
      const value = params[accessorKey]
      if (value === 'true' || value === true) return 'Yes'
      if (value === 'false' || value === false) return 'No'
      if (value === 'null') return 'Unknown'
    }

    // For basic filters
    if (column.filter_type === 'basic') {
      const operatorKey = `${accessorKey}Operator`
      const operator = params[operatorKey] || 'contains'
      const value = params[accessorKey]

      if (operator === 'is_empty') return 'Is empty'
      if (operator === 'is_not_empty') return 'Is not empty'
      if (value) return `Contains "${value}"`
    }

    // For select, multiselect, and mcc_select
    if (['select', 'multiselect', 'mcc_select'].includes(column.filter_type)) {
      const value = params[accessorKey]
      if (Array.isArray(value) && value.length > 0) {
        if (value.length === 1) {
          // Handle case where array item is an object (e.g., analytics)
          const item = value[0]
          if (typeof item === 'object' && item !== null) {
            return item.name || item.label || JSON.stringify(item)
          }
          return item
        }
        return `${value.length} selected`
      } else if (value) {
        // Handle case where value itself is an object
        if (typeof value === 'object' && value !== null) {
          return value.name || value.label || JSON.stringify(value)
        }
        return value
      }
    }

    // For greater_than
    if (column.filter_type === 'greater_than') {
      const value = params[minKey] || params[accessorKey]
      if (value) return `≥ ${value}`
    }

    // For reference filters
    if (column.filter_type === 'reference') {
      const value = params[accessorKey]
      if (value) {
        // Handle case where value is an object
        if (typeof value === 'object' && value !== null) {
          return value.name || value.label || JSON.stringify(value)
        }
        return value
      }
    }

    return 'Not set'
  }, [column, params])

  const renderFilterContent = () => {
    const popoverOptions =
      options.length > 0
        ? options
        : getOptions
        ? getOptions(column.accessor_key)
        : []

    // Render filter components inline (no popover, no close button)
    return (
      <div>
        {column.filter_type === 'basic' && (
          <BasicFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'boolean' && (
          <BooleanFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'date' && (
          <DateFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {(column.filter_type === 'min_max' ||
          column.filter_type === 'min_max_percent') && (
          <MinMaxFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'select' && (
          <SelectFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            options={popoverOptions}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'multiselect' && (
          <MultiSelectFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            options={popoverOptions}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'mcc_select' && (
          <MccSelectFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'greater_than' && (
          <GreaterThanFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            isUserCreator={isUserCreator}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        )}
        {column.filter_type === 'reference' && column.database_column_name === 'vendor_name' ? (
          <div className="mb-3">
            <div className="mb-3">
              <strong>
                {label}
                {requiresChange && <span className="text-danger"> *</span>}
              </strong>
            </div>
            <VendorSearch
              params={params}
              setParams={setParams}
            />
          </div>
        ) : column.filter_type === 'reference' ? (
          <ReferenceFilterPopover
            column={column}
            params={params}
            setParams={setParams}
            onClose={() => {}}
            onSetHandleApply={(handleApply) => {
              childHandleApplyRef.current = handleApply
            }}
            disableClear={requiresChange}
            requiresChange={requiresChange}
            hideApplyButton={hideApplyButton}
          />
        ) : null}
      </div>
    )
  }


  return (
    <div className="mb-2">
      {/* Accordion Header */}
      <div
        className={`d-flex justify-content-between align-items-center p-3 border rounded ${
          requiresChange && !hasBeenModified ? 'border-warning' : ''
        }`}
        style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f8f9fa' : 'white' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="d-flex align-items-center gap-3 flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <strong>{label}</strong>
            {requiresChange && !hasBeenModified && (
              <Badge bg="warning" text="dark" className="small">
                Required
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="text-muted small">{formatFilterValue}</div>
          )}
        </div>
        <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-muted" />
      </div>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="p-3 border border-top-0 rounded-bottom">
          {renderFilterContent()}
        </div>
      )}
    </div>
  )
}

export default FilterEditor
